import React, { useState, useEffect, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { ScreenshotGallery, ScreenshotPreview } from "./ScreenshotGallery";

type Page = "home" | "contact" | "screenshots";

/* ───── SVG icon helpers ───── */

const CheckIcon = ({ size = 16, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = ({ size = 18, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" className={className}>
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/* ───── Navbar ───── */

function Navbar({ onNavigate }: { onNavigate: (p: Page) => void }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const goHome = () => { onNavigate("home"); setOpen(false); };
  const goSection = (id: string) => { goHome(); setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 100); };

  type NavLink = { id: string; label: string; page?: Page };

  const links: NavLink[] = [
    { id: "avantages", label: "Avantages" },
    { id: "fonctionnalites", label: "Fonctionnalités" },
    { id: "captures", label: "Captures d'écran", page: "screenshots" },
    { id: "tarif", label: "Tarif" },
    { id: "faq", label: "FAQ" },
  ];

  const handleLink = (l: NavLink) => {
    if (l.page) { onNavigate(l.page); setOpen(false); }
    else goSection(l.id);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/92 backdrop-blur-xl border-b border-slate-200">
      <div className="max-w-[1200px] mx-auto px-6 flex items-center justify-between h-[72px]">
        <button onClick={goHome} className="flex items-center gap-2.5 font-extrabold text-[1.4rem] text-slate-900 cursor-pointer bg-transparent border-none">
          <span className="text-3xl">🍽️</span>
          cantine<span className="text-[#D3A885]">.</span>appitep<span className="text-[#D3A885]">.</span>fr
        </button>

        {/* Mobile hamburger */}
        <button
          className="md:hidden flex flex-col gap-[5px] p-2 z-[1001] bg-transparent border-none cursor-pointer relative"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          <span className={`w-6 h-0.5 bg-slate-900 block transition-all ${open ? "rotate-45 translate-y-[7px]" : ""}`} />
          <span className={`w-6 h-0.5 bg-slate-900 block transition-all ${open ? "opacity-0" : ""}`} />
          <span className={`w-6 h-0.5 bg-slate-900 block transition-all ${open ? "-rotate-45 -translate-y-[7px]" : ""}`} />
        </button>

        {/* Desktop links */}
        <ul className="hidden md:flex items-center gap-8 list-none">
          {links.map(l => (
            <li key={l.id}>
              <button onClick={() => handleLink(l)} className="text-slate-500 font-medium text-[0.95rem] hover:text-slate-900 transition-colors cursor-pointer bg-transparent border-none">{l.label}</button>
            </li>
          ))}
          <li>
            <a href="https://appitep.fr" target="_blank" rel="noopener noreferrer" className="text-slate-500 font-medium text-[0.95rem] hover:text-slate-900 transition-colors cursor-pointer">Qui sommes-nous&nbsp;?</a>
          </li>
          <li>
            <button onClick={() => { onNavigate("contact"); setOpen(false); }} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-[0.9rem] font-bold bg-[#D3A885] text-white shadow-[0_4px_14px_rgba(211,168,133,0.35)] hover:bg-[#c49b78] hover:translate-y-[-2px] hover:shadow-[0_6px_20px_rgba(211,168,133,0.4)] transition-all cursor-pointer border-none">
              Nous contacter
            </button>
          </li>
        </ul>
      </div>

      {/* Mobile drawer */}
      {createPortal(
        <>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-black/40 z-[9990] transition-opacity duration-300 md:hidden ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            onClick={() => setOpen(false)}
          />
          {/* Drawer */}
          <div
            className={`fixed top-0 right-0 bottom-0 w-[280px] bg-white shadow-2xl z-[9991] flex flex-col transition-transform duration-300 ease-in-out md:hidden ${open ? "translate-x-0" : "translate-x-full"}`}
          >
            <div className="flex justify-end p-4">
              <button onClick={() => setOpen(false)} className="p-2 bg-transparent border-none cursor-pointer text-slate-500 hover:text-slate-900" aria-label="Fermer">
                <XIcon size={24} />
              </button>
            </div>
            <ul className="flex flex-col gap-1 px-4 list-none m-0">
              {links.map(l => (
                <li key={l.id}>
                  <button onClick={() => handleLink(l)} className="w-full text-left text-slate-600 font-medium text-[1.05rem] py-3 px-4 rounded-xl hover:bg-slate-50 hover:text-slate-900 transition-colors cursor-pointer bg-transparent border-none">{l.label}</button>
                </li>
              ))}
            </ul>
            <div className="mt-auto p-6 flex flex-col gap-3">
              <a href="https://appitep.fr" target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className="w-full text-left text-slate-600 font-medium text-[1.05rem] py-3 px-4 rounded-xl hover:bg-slate-50 hover:text-slate-900 transition-colors no-underline">Qui sommes-nous&nbsp;?</a>
              <button onClick={() => { onNavigate("contact"); setOpen(false); }} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-[0.95rem] font-bold bg-[#D3A885] text-white shadow-[0_4px_14px_rgba(211,168,133,0.35)] cursor-pointer border-none">
                Nous contacter
              </button>
            </div>
          </div>
        </>,
        document.body,
      )}
    </nav>
  );
}

/* ───── Hero ───── */

function Hero({ onNavigate }: { onNavigate: (p: Page) => void }) {
  return (
    <section className="pt-[90px] pb-14 px-6 bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50 relative overflow-hidden" id="accueil">
      <div className="absolute -top-1/2 -right-[20%] w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,_rgba(211,168,133,0.08)_0%,_transparent_70%)] pointer-events-none" />
      <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 items-center">
        <div className="flex justify-center relative z-[1] lg:justify-end order-first lg:order-last">
          <img
            src="/cantine_hero_bg.jpg"
            alt="Un parent utilise l'application cantine sur son téléphone pendant que son enfant court joyeusement"
            className="w-full max-w-[560px] lg:max-w-none lg:w-[110%] h-auto rounded-3xl shadow-[0_25px_80px_rgba(0,0,0,0.18)] object-cover"
          />
        </div>
        <div className="relative z-[1] lg:text-left text-center">
          <h1 className="text-[clamp(2rem,5vw,3.5rem)] font-extrabold text-slate-900 leading-[1.2] tracking-tight mb-5">
            Votre cantine scolaire mérite un outil moderne.
          </h1>
          <p className="text-[clamp(1.05rem,2vw,1.2rem)] text-slate-500 mb-3 leading-relaxed">
            Fini les listes papier, les tableaux Excel<br />et les appels téléphoniques.<br />
            Offrez aux familles un moyen simple de réserver et payer les repas en ligne.
          </p>
          <div className="text-[#D3A885] mb-8 flex items-center gap-2 lg:justify-start justify-center whitespace-nowrap">
            <strong className="text-[1.4rem] font-extrabold">348€/an</strong>&nbsp;<span className="text-[0.85rem] font-medium">soit 29€/mois, tout inclus.</span>
          </div>
          <div className="flex gap-4 mb-8 flex-wrap lg:justify-start justify-center">
            <button onClick={() => onNavigate("contact")} className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-[1.05rem] font-bold bg-[#D3A885] text-white shadow-[0_4px_14px_rgba(211,168,133,0.35)] hover:bg-[#c49b78] hover:translate-y-[-2px] hover:shadow-[0_6px_20px_rgba(211,168,133,0.4)] transition-all cursor-pointer border-none">
              Demander une démo gratuite →
            </button>
            <a href="#avantages" className="inline-flex items-center gap-2 px-6 py-4 rounded-full text-[1.05rem] font-bold text-slate-500 hover:text-[#D3A885] transition-all no-underline">
              Voir les avantages
            </a>
          </div>
          <div className="flex gap-6 text-[0.9rem] text-slate-500 flex-wrap lg:justify-start justify-center">
            <span className="flex items-center gap-1.5"><CheckIcon size={16} className="text-emerald-500" /> Élèves illimités</span>
            <span className="flex items-center gap-1.5"><CheckIcon size={16} className="text-emerald-500" /> Support inclus</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── Target Audience ───── */

function TargetAudience() {
  const badges = [
    "🏫 École primaire", "🏛️ Mairie", "👨‍👩‍👧‍👦 Association de parents",
    "🏕️ Centre de loisirs", "🧒 Crèche", "🍽️ Restauration scolaire",
  ];
  return (
    <section className="py-14 px-6 bg-slate-50" id="pour-qui">
      <div className="text-center mb-4">
        <h2 className="text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold text-slate-900 leading-[1.2] tracking-tight mb-4">
          Conçu pour les cantines scolaires<br />et les collectivités.
        </h2>
        <p className="text-[clamp(1.05rem,2vw,1.25rem)] text-slate-500 max-w-[680px] mx-auto mb-12 leading-relaxed">
          Que vous gériez une cantine d'école, un centre de loisirs ou une crèche, notre logiciel s'adapte.
        </p>
        <div className="flex flex-wrap justify-center gap-3 max-w-[800px] mx-auto">
          {badges.map(b => (
            <span key={b} className="px-5 py-2.5 rounded-full bg-white border border-slate-200 text-[0.95rem] font-medium text-slate-700 shadow-sm">{b}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── Before / After ───── */

function BeforeAfter() {
  const before = [
    "Listes papier et cahiers à recopier",
    "Appels téléphoniques des parents pour réserver",
    "Facturation manuelle sous Excel chaque mois",
    "Comptage des repas à la main",
    "Aucun suivi des allergies centralisé",
    "Pas de visibilité sur les paiements",
  ];
  const after = [
    "Les parents réservent en ligne en 2 clics",
    "Facturation automatique à partir des réservations",
    "Tableau de bord avec le nombre de repas du jour",
    "Paiement en ligne sécurisé par carte bancaire",
    "Fiche allergie par enfant, visible par l'équipe",
    "Suivi des paiements en temps réel",
  ];

  return (
    <section className="py-14 px-6 scroll-mt-20" id="avantages">
      <div className="text-center mb-12">
        <h2 className="text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold text-slate-900 leading-[1.2] tracking-tight mb-4">
          Sans logiciel vs. avec.
        </h2>
        <p className="text-[clamp(1.05rem,2vw,1.25rem)] text-slate-500 max-w-[680px] mx-auto leading-relaxed">
          La différence au quotidien quand la gestion de votre cantine passe au numérique.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-8 max-w-[960px] mx-auto items-stretch">
        {/* Sans */}
        <div className="rounded-2xl p-9 bg-red-50 border-2 border-red-200">
          <h3 className="text-xl font-extrabold text-red-500 mb-5 flex items-center gap-2.5">
            <XIcon size={22} className="text-red-500" /> Sans logiciel
          </h3>
          <ul className="list-none flex flex-col gap-3.5">
            {before.map(item => (
              <li key={item} className="flex items-start gap-2.5 text-[0.95rem] leading-relaxed">
                <XIcon size={18} className="text-red-500 shrink-0 mt-0.5" /><span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center justify-center py-2 lg:py-0">
          <div className="w-14 h-14 rounded-full bg-[#D3A885] text-white flex items-center justify-center text-[1.1rem] font-black shadow-[0_4px_14px_rgba(211,168,133,0.35)]">VS</div>
        </div>
        {/* Avec */}
        <div className="rounded-2xl p-9 bg-emerald-50 border-2 border-emerald-200">
          <h3 className="text-xl font-extrabold text-emerald-500 mb-5 flex items-center gap-2.5">
            <CheckIcon size={22} className="text-emerald-500" /> Avec votre logiciel cantine
          </h3>
          <ul className="list-none flex flex-col gap-3.5">
            {after.map(item => (
              <li key={item} className="flex items-start gap-2.5 text-[0.95rem] leading-relaxed">
                <CheckIcon size={18} className="text-emerald-500 shrink-0 mt-0.5" /><span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ───── Key Benefits ───── */

function Benefits() {
  const items = [
    { icon: "⏱️", title: "Gain de temps considérable", desc: "Fini la saisie manuelle. Les parents réservent eux-mêmes en ligne, la facturation est automatique à partir des réservations." },
    { icon: "💳", title: "Paiement en ligne sécurisé", desc: "Les familles paient par carte bancaire via Stripe. Suivi des paiements en temps réel, relances simplifiées." },
    { icon: "📊", title: "Vision claire de votre activité", desc: "Combien de repas aujourd'hui ? Ce mois-ci ? Par classe ? Par tranche tarifaire ? Tout est dans le tableau de bord." },
    { icon: "🧾", title: "Moins d'erreurs", desc: "Les réservations sont faites par les parents. La facturation est générée automatiquement. Plus de recopie, plus d'oubli." },
    { icon: "🥗", title: "Allergies & régimes suivis", desc: "Chaque fiche enfant contient les allergies et remarques. L'équipe de cantine a accès à l'info en temps réel." },
    { icon: "📱", title: "Accessible partout", desc: "Application web installable sur mobile (PWA). Les parents gèrent tout depuis leur téléphone, à tout moment." },
  ];

  return (
    <section className="py-14 px-6 bg-slate-50">
      <div className="text-center mb-12">
        <h2 className="text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold text-slate-900 leading-[1.2] tracking-tight mb-4">
          Pourquoi passer au numérique ?
        </h2>
        <p className="text-[clamp(1.05rem,2vw,1.25rem)] text-slate-500 max-w-[680px] mx-auto leading-relaxed">
          Des avantages concrets pour votre cantine, dès le premier jour.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1000px] mx-auto">
        {items.map(it => (
          <div key={it.title} className="flex items-start gap-4 p-6 rounded-xl bg-white border border-slate-200 shadow-sm">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center text-[1.4rem] shrink-0 bg-amber-50">{it.icon}</div>
            <div>
              <h3 className="text-base font-bold text-slate-900 mb-1">{it.title}</h3>
              <p className="text-[0.88rem] text-slate-500 leading-relaxed">{it.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───── Pillars ───── */

function Pillars() {
  const pillars = [
    { icon: "🎯", color: "bg-blue-50 text-blue-500", title: "Simplicité totale", desc: "Interface intuitive pour les parents comme pour l'équipe de gestion. Réserver un repas prend 10 secondes. Pas besoin de formation." },
    { icon: "💰", color: "bg-emerald-50 text-emerald-500", title: "Prix juste, tout inclus", desc: "348€/an — c'est tout. Pas de coût par élève, pas de module payant en plus. Nombre illimité d'élèves, toutes les fonctionnalités, support inclus." },
    { icon: "🔒", color: "bg-amber-50 text-[#D3A885]", title: "Données sécurisées", desc: "Hébergement en France, HTTPS, paiements Stripe certifiés PCI-DSS. Les données des familles sont protégées et ne sont jamais partagées." },
  ];

  return (
    <section className="py-14 px-6" id="solution">
      <div className="text-center mb-12">
        <h2 className="text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold text-slate-900 leading-[1.2] tracking-tight mb-4">
          Simple, complet, abordable.
        </h2>
        <p className="text-[clamp(1.05rem,2vw,1.25rem)] text-slate-500 max-w-[680px] mx-auto leading-relaxed">
          Un logiciel clé en main pensé pour les petites et moyennes structures. Pas besoin d'être informaticien.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-[1200px] mx-auto">
        {pillars.map(p => (
          <div key={p.title} className="bg-white rounded-2xl p-9 shadow-md border border-slate-200 transition-all hover:-translate-y-1 hover:shadow-lg">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-[1.6rem] mb-5 ${p.color}`}>{p.icon}</div>
            <h3 className="text-[clamp(1.25rem,2.5vw,1.5rem)] font-extrabold text-slate-900 mb-2">{p.title}</h3>
            <p className="text-slate-500 leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───── Features Grid ───── */

function Features() {
  const features = [
    { icon: "📅", title: "Réservation des repas", desc: "Les parents réservent les repas de leurs enfants via un calendrier interactif. Sélection multi-jours, gestion par enfant." },
    { icon: "🍽️", title: "Menus du jour", desc: "Publiez le menu du jour (entrée, plat, dessert) avec les ingrédients. Les parents consultent le menu à l'avance." },
    { icon: "🧾", title: "Facturation automatique", desc: "Génération automatique des factures mensuelles à partir des réservations. Export PDF, envoi par email, export Excel." },
    { icon: "💳", title: "Paiement par carte (Stripe)", desc: "Les familles paient en ligne en toute sécurité via Stripe Checkout. Confirmation automatique, suivi des paiements." },
    { icon: "👨‍👩‍👧‍👦", title: "Gestion des familles", desc: "Fiche famille complète : enfants, classe, allergies, remarques, coordonnées. Tarification par tranche de revenus." },
    { icon: "📊", title: "Tableaux de bord", desc: "Dashboard quotidien (repas du jour, allergies) et mensuel (décompte par classe). Export Excel et feuille de pointage PDF." },
    { icon: "📄", title: "Documents & attestations", desc: "Espace documents pour partager des fichiers aux familles : règlement, attestations, formulaires d'inscription." },
    { icon: "📱", title: "PWA installable", desc: "Application web progressive installable sur téléphone comme une app native. Fonctionne sur Android, iPhone et desktop." },
    { icon: "📢", title: "Messages & notifications", desc: "Publiez des messages sur la page d'accueil. Notifications email pour les factures. Alertes quotidiennes automatiques." },
    { icon: "🗓️", title: "Gestion des fermetures", desc: "Définissez les jours de fermeture (vacances, jours fériés). Les parents ne peuvent pas réserver sur ces dates." },
    { icon: "🎥", title: "Tutoriels vidéo intégrés", desc: "Vidéos d'aide intégrées : installation mobile, création de compte, paiement par carte. Accessibles depuis l'application." },
    { icon: "📧", title: "Contact & support", desc: "Page de contact intégrée pour les familles. L'équipe reçoit les messages directement par email." },
  ];

  return (
    <section className="py-14 px-6 bg-slate-50 scroll-mt-20" id="fonctionnalites">
      <div className="text-center mb-12">
        <h2 className="text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold text-slate-900 leading-[1.2] tracking-tight mb-4">
          Tout ce dont votre cantine a besoin.<br />Rien de superflu.
        </h2>
        <p className="text-[clamp(1.05rem,2vw,1.25rem)] text-slate-500 max-w-[680px] mx-auto leading-relaxed">
          Chaque fonctionnalité a été pensée pour les vrais besoins des gestionnaires de cantine et des familles.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1200px] mx-auto">
        {features.map(f => (
          <div key={f.title} className="p-7 rounded-xl bg-white border border-slate-200 transition-all hover:border-[#D3A885] hover:shadow-md">
            <span className="text-[1.6rem] mb-3 block">{f.icon}</span>
            <h3 className="text-[1.05rem] font-bold text-slate-900 mb-2">{f.title}</h3>
            <p className="text-[0.92rem] text-slate-500 leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───── Steps ───── */

function Steps() {
  const steps = [
    { n: 1, title: "On en parle", desc: "Un échange pour comprendre votre cantine : nombre d'élèves, tarifs, fonctionnement. Par téléphone, visio ou email." },
    { n: 2, title: "On configure", desc: "On paramètre votre application : vos élèves, vos tarifs, vos classes, vos menus. Vous validez avant mise en ligne." },
    { n: 3, title: "C'est en ligne", desc: "Votre cantine est digitalisée. Les parents reçoivent leurs identifiants et peuvent commencer à réserver." },
    { n: 4, title: "Vous gérez", desc: "Suivez les réservations, générez les factures, consultez les tableaux de bord. On reste disponible pour le support." },
  ];

  return (
    <section className="py-14 px-6" id="etapes">
      <div className="text-center mb-12">
        <h2 className="text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold text-slate-900 leading-[1.2] tracking-tight mb-4">
          Opérationnel en 48h.
        </h2>
        <p className="text-[clamp(1.05rem,2vw,1.25rem)] text-slate-500 max-w-[680px] mx-auto leading-relaxed">
          De l'échange initial à la mise en ligne, tout se fait rapidement et simplement.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-[1200px] mx-auto text-center">
        {steps.map((s, i) => (
          <div key={s.n} className="relative">
            {i < steps.length - 1 && (
              <span className="hidden lg:block absolute -right-5 top-9 text-2xl text-slate-300 font-light">→</span>
            )}
            <div className="w-14 h-14 rounded-full bg-[#D3A885] text-white flex items-center justify-center text-[1.4rem] font-extrabold mx-auto mb-4">{s.n}</div>
            <h3 className="text-[clamp(1.25rem,2.5vw,1.5rem)] font-extrabold text-slate-900 mb-2">{s.title}</h3>
            <p className="text-[0.92rem] text-slate-500 leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───── Testimonials ───── */

function Testimonials() {
  const items = [
    { text: "Avant, je passais des heures chaque mois à recompter les repas et faire les factures sur Excel. Maintenant tout est automatique. Les parents adorent pouvoir réserver depuis leur téléphone.", name: "Babeth", role: "Gestionnaire de cantine — Monoblet", initial: "B" },
    { text: "Les parents ont adopté l'outil très vite. Ils apprécient de voir le menu du jour et de pouvoir payer par carte. On a beaucoup moins de chèques à gérer.", name: "Chloé", role: "Équipe cantine — Monoblet", initial: "C" },
    { text: "En tant que parent, c'est un vrai confort. Je réserve les repas de mes enfants en 30 secondes, je vois le menu, et je paie en ligne. Plus besoin de penser au chèque.", name: "Sophie", role: "Parent d'élèves — Monoblet", initial: "S" },
  ];

  return (
    <section className="py-14 px-6 bg-slate-50" id="temoignages">
      <div className="text-center mb-12">
        <h2 className="text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold text-slate-900 leading-[1.2] tracking-tight mb-4">
          Déjà adopté sur le terrain.
        </h2>
        <p className="text-[clamp(1.05rem,2vw,1.25rem)] text-slate-500 max-w-[680px] mx-auto leading-relaxed">
          Le logiciel est utilisé au quotidien par la cantine de l'école de Monoblet depuis 2023.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-7 max-w-[1200px] mx-auto">
        {items.map(t => (
          <div key={t.name} className="bg-white rounded-2xl p-8 shadow-md border border-slate-200 relative">
            <span className="absolute top-4 left-6 text-5xl text-[#D3A885] opacity-20 leading-none">❝</span>
            <p className="text-base leading-relaxed text-slate-800 mb-5 italic">{t.text}</p>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#D3A885] to-[#c49b78] flex items-center justify-center text-white font-bold text-lg">{t.initial}</div>
              <div>
                <div className="font-bold text-[0.95rem]">{t.name}</div>
                <div className="text-[0.82rem] text-slate-500">{t.role}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───── Pricing ───── */

function Pricing({ onNavigate }: { onNavigate: (p: Page) => void }) {
  const included = [
    "Réservation des repas en ligne",
    "Facturation automatique",
    "Paiement par carte bancaire (Stripe)",
    "Menus du jour & ingrédients",
    "Gestion des familles & allergies",
    "Tableaux de bord quotidien & mensuel",
    "Documents & attestations",
    "PWA installable (mobile & desktop)",
    "Notifications & messages",
    "Tutoriels vidéo",
    "Support technique & mises à jour",
    "Nombre illimité d'élèves",
  ];
  const notIncluded = [
    "Aucun coût par élève",
    "Aucun frais caché",
    "Aucun engagement — résiliez quand vous voulez",
  ];

  return (
    <section className="py-14 px-6 scroll-mt-20" id="tarif">
      <div className="text-center mb-12">
        <h2 className="text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold text-slate-900 leading-[1.2] tracking-tight mb-4">
          Un prix simple. Tout inclus.
        </h2>
        <p className="text-[clamp(1.05rem,2vw,1.25rem)] text-slate-500 max-w-[680px] mx-auto leading-relaxed">
          Pas de module en supplément. Un seul tarif, toutes les fonctionnalités.
        </p>
      </div>
      <div className="max-w-[520px] mx-auto bg-white rounded-3xl p-12 max-sm:p-6 shadow-xl border-2 border-[#D3A885] text-center relative">
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#D3A885] text-white text-[0.82rem] font-bold px-5 py-1.5 rounded-full whitespace-nowrap">
          Tarif tout inclus
        </div>
        <div className="flex items-baseline justify-center gap-1 mb-2">
          <span className="text-[clamp(3rem,8vw,4.5rem)] font-black text-slate-900 leading-none">348€</span>
          <span className="text-[1.1rem] text-slate-500">/an</span>
        </div>
        <div className="text-[0.9rem] text-slate-400 mb-8">soit 29€/mois · nombre illimité d'élèves</div>
        <ul className="list-none text-left mb-8">
          {included.map(f => (
            <li key={f} className="py-2.5 border-b border-slate-200 last:border-b-0 flex items-center gap-3 text-[0.95rem]">
              <CheckIcon size={20} className="text-emerald-500 shrink-0" /> {f}
            </li>
          ))}
        </ul>
        <ul className="list-none text-left mb-8 pt-3 border-t-2 border-dashed border-slate-200">
          {notIncluded.map(f => (
            <li key={f} className="py-1.5 flex items-center gap-3 text-[0.9rem] text-slate-500">
              <XIcon size={18} className="text-slate-400 shrink-0" /> {f}
            </li>
          ))}
        </ul>
        <button onClick={() => onNavigate("contact")} className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-[1.05rem] font-bold bg-[#D3A885] text-white shadow-[0_4px_14px_rgba(211,168,133,0.35)] hover:bg-[#c49b78] hover:translate-y-[-2px] hover:shadow-[0_6px_20px_rgba(211,168,133,0.4)] transition-all cursor-pointer border-none">
          Demander une démo gratuite →
        </button>
      </div>
    </section>
  );
}

/* ───── Comparison Table ───── */

function Comparison() {
  const rows = [
    { label: "Réservation en ligne", them: "❌ Papier / téléphone", us: "✅ Calendrier interactif" },
    { label: "Facturation", them: "❌ Excel manuel", us: "✅ Automatique" },
    { label: "Paiement", them: "❌ Chèques uniquement", us: "✅ Carte bancaire en ligne" },
    { label: "Menu du jour", them: "❌ Affichage papier", us: "✅ Consultable en ligne" },
    { label: "Suivi allergies", them: "❌ Fiches papier", us: "✅ Fiche numérique par enfant" },
    { label: "Tableaux de bord", them: "❌ Comptage manuel", us: "✅ Dashboard temps réel" },
  ];

  return (
    <section className="py-14 px-6 bg-slate-50" id="comparaison">
      <div className="text-center mb-12">
        <h2 className="text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold text-slate-900 leading-[1.2] tracking-tight mb-4">
          Gestion papier vs. logiciel cantine.
        </h2>
        <p className="text-[clamp(1.05rem,2vw,1.25rem)] text-slate-500 max-w-[680px] mx-auto leading-relaxed">
          Voici ce qui change concrètement quand vous passez au numérique.
        </p>
      </div>
      <div className="max-w-[700px] mx-auto overflow-x-auto">
        <table className="w-full border-collapse rounded-xl overflow-hidden shadow-md text-[0.9rem]">
          <thead>
            <tr>
              <th className="py-3.5 px-5 text-left bg-slate-800 text-white font-bold text-[0.92rem]" />
              <th className="py-3.5 px-5 text-left bg-slate-900 text-white font-bold text-[0.92rem]">Gestion papier / Excel</th>
              <th className="py-3.5 px-5 text-left bg-emerald-500 text-white font-bold text-[0.92rem]">Notre logiciel</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.label} className={`border-b border-slate-200 last:border-b-0 ${i % 2 === 1 ? "bg-slate-50" : ""}`}>
                <td className="py-3.5 px-5 font-semibold text-slate-900">{r.label}</td>
                <td className="py-3.5 px-5 text-red-500">{r.them}</td>
                <td className="py-3.5 px-5 text-emerald-600 font-medium">{r.us}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ───── FAQ ───── */

function FAQ() {
  const items = [
    { q: "Combien coûte le logiciel ?", a: "348€/an tout inclus, soit 29€/mois. Nombre illimité d'élèves, toutes les fonctionnalités, support technique et mises à jour compris. Pas de coût par élève, pas de frais cachés." },
    { q: "En combien de temps le logiciel est opérationnel ?", a: "48 heures. On s'occupe de la configuration initiale (vos élèves, vos tarifs, vos classes), vous validez, et c'est en ligne. Les parents reçoivent leurs identifiants et peuvent commencer à réserver." },
    { q: "Les données sont-elles sécurisées ?", a: "Oui. Le paiement est géré par Stripe (certifié PCI-DSS niveau 1). L'authentification utilise des tokens JWT sécurisés. Le serveur est en HTTPS. Les données sont hébergées en France et ne sont ni partagées ni revendues." },
    { q: "Le logiciel fonctionne-t-il sur mobile ?", a: "Oui. C'est une application web progressive (PWA) que les parents peuvent installer sur leur téléphone comme une application native, sans passer par un store. Elle fonctionne sur Android, iPhone et desktop." },
    { q: "Le logiciel est-il déjà utilisé ?", a: "Oui. Il est en production depuis 2023 à la cantine de l'école publique de Monoblet (30). Les parents, l'équipe de cantine et la mairie l'utilisent au quotidien." },
    { q: "Peut-on gérer des tarifs différents selon les familles ?", a: "Oui. Le système permet de définir des tranches tarifaires basées sur le quotient familial ou les revenus. Chaque famille se voit attribuer le tarif correspondant." },
    { q: "Comment les parents paient-ils ?", a: "Par carte bancaire en ligne (Stripe), mais aussi par chèque ou virement. L'administrateur peut enregistrer tous les modes de paiement dans le système." },
    { q: "Faut-il un engagement ?", a: "Non. Vous pouvez résilier à tout moment, sans frais. L'abonnement est annuel mais sans engagement de durée." },
  ];

  return (
    <section className="py-14 px-6 scroll-mt-20" id="faq">
      <div className="text-center mb-12">
        <h2 className="text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold text-slate-900 leading-[1.2] tracking-tight mb-4">
          Questions fréquentes
        </h2>
        <p className="text-[clamp(1.05rem,2vw,1.25rem)] text-slate-500 max-w-[680px] mx-auto leading-relaxed">
          Tout ce que vous voulez savoir avant de vous lancer.
        </p>
      </div>
      <div className="max-w-[760px] mx-auto">
        {items.map(it => (
          <details key={it.q} className="border-b border-slate-200 group">
            <summary className="py-5 text-[1.05rem] font-semibold text-slate-900 cursor-pointer list-none flex items-center justify-between gap-4 [&::-webkit-details-marker]:hidden">
              {it.q}
              <span className="text-2xl font-light text-slate-500 shrink-0 transition-transform group-open:rotate-45">+</span>
            </summary>
            <p className="pb-5 text-slate-500 leading-relaxed text-[0.95rem]">{it.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

/* ───── Final CTA ───── */

function FinalCTA({ onNavigate }: { onNavigate: (p: Page) => void }) {
  return (
    <section className="py-14 px-6 bg-gradient-to-br from-slate-900 to-[#3b2f1e] text-white text-center" id="contact">
      <div className="max-w-[1200px] mx-auto">
        <h2 className="text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold text-white leading-[1.2] tracking-tight mb-4">
          Digitalisez votre cantine.<br />Les familles vous remercieront.
        </h2>
        <p className="text-slate-400 text-[1.15rem] mb-8 max-w-[600px] mx-auto">
          Rejoignez les cantines qui ont simplifié leur gestion avec un outil moderne, simple et abordable.
        </p>
        <button onClick={() => onNavigate("contact")} className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-[1.05rem] font-bold bg-white text-slate-900 shadow-md hover:translate-y-[-2px] hover:shadow-lg transition-all cursor-pointer border-none">
          Demander une démo gratuite →
        </button>
        <div className="flex justify-center gap-8 mt-8 text-[0.9rem] text-slate-400 flex-wrap max-sm:flex-col max-sm:items-center max-sm:gap-3">
          <span className="flex items-center gap-1.5"><CheckIcon size={16} /> 348€/an tout inclus</span>
          <span className="flex items-center gap-1.5"><CheckIcon size={16} /> Opérationnel en 48h</span>
          <span className="flex items-center gap-1.5"><CheckIcon size={16} /> Élèves illimités</span>
          <span className="flex items-center gap-1.5"><CheckIcon size={16} /> Support inclus</span>
        </div>
      </div>
    </section>
  );
}

/* ───── Contact Form ───── */

function ContactForm({ onNavigate }: { onNavigate: (p: Page) => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message, website }),
      });

      const data = await res.json();

      if (!res.ok) {
        setStatus("error");
        setErrorMsg(data.error || "Une erreur est survenue.");
        return;
      }

      setStatus("success");
    } catch {
      setStatus("error");
      setErrorMsg("Impossible de contacter le serveur. Réessayez plus tard.");
    }
  };

  if (status === "success") {
    return (
      <section className="pt-[140px] pb-20 px-6 min-h-screen flex items-center justify-center">
        <div className="max-w-lg mx-auto text-center">
          <div className="text-6xl mb-6">✉️</div>
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Message envoyé&nbsp;!</h2>
          <p className="text-slate-500 mb-8">
            Merci pour votre message. Nous vous répondrons dans les meilleurs délais.
          </p>
          <button
            onClick={() => onNavigate("home")}
            className="bg-[#D3A885] hover:bg-[#c49b78] text-white font-semibold px-8 py-3 rounded-full transition-colors cursor-pointer border-none"
          >
            Retour à l'accueil
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-[140px] pb-20 px-6">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => onNavigate("home")}
          className="inline-flex items-center gap-2 text-slate-500 hover:text-[#D3A885] mb-8 transition-colors cursor-pointer bg-transparent border-none"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Retour
        </button>

        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-2">
          Contactez-<span className="text-[#D3A885]">nous</span>
        </h2>
        <p className="text-slate-500 mb-10">
          Décrivez votre projet en quelques mots. Nous vous répondrons rapidement.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot — hidden from users */}
          <div className="absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              type="text"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-slate-900 mb-2">
              Votre nom *
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={200}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#D3A885] focus:ring-2 focus:ring-[#D3A885]/20 outline-none transition-all text-slate-700"
              placeholder="Jean Dupont"
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-slate-900 mb-2">
              Votre email *
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              maxLength={320}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#D3A885] focus:ring-2 focus:ring-[#D3A885]/20 outline-none transition-all text-slate-700"
              placeholder="jean@exemple.fr"
            />
          </div>

          {/* Message */}
          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-slate-900 mb-2">
              Votre message
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={5000}
              rows={5}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#D3A885] focus:ring-2 focus:ring-[#D3A885]/20 outline-none transition-all resize-y text-slate-700"
              placeholder="Parlez-nous de votre cantine : nombre d'élèves, fonctionnement actuel..."
            />
          </div>

          {/* Error */}
          {status === "error" && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
              {errorMsg}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={status === "sending"}
            className="w-full bg-[#D3A885] hover:bg-[#c49b78] text-white font-bold py-4 px-8 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer border-none text-[1.05rem]"
          >
            {status === "sending" ? "Envoi en cours..." : "Envoyer le message →"}
          </button>
        </form>
      </div>
    </section>
  );
}

/* ───── Footer ───── */

function Footer() {
  return (
    <footer className="bg-slate-900 text-slate-400 pt-12 pb-8 px-6">
      <div className="max-w-[1200px] mx-auto flex justify-between items-start flex-wrap gap-8 max-md:flex-col">
        <div>
          <div className="text-xl font-extrabold text-white mb-2">
            cantine<span className="text-[#D3A885]">.</span>appitep<span className="text-[#D3A885]">.</span>fr
          </div>
          <p className="text-[0.88rem] text-slate-400 max-w-[320px] leading-relaxed">
            Logiciel complet de gestion de cantine scolaire. Réservation, facturation, menus, paiement en ligne. 348€/an tout inclus.
          </p>
        </div>
        <div>
          <ul className="list-none flex flex-col gap-2">
            {["Avantages", "Fonctionnalités", "Tarif", "FAQ", "Contact"].map(l => (
              <li key={l}><a href={`#${l.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")}`} className="text-slate-400 text-[0.9rem] hover:text-white transition-colors no-underline">{l}</a></li>
            ))}
          </ul>
        </div>
        <div>
          <ul className="list-none flex flex-col gap-2">
            <li><a href="mailto:contact@appitep.fr" className="text-slate-400 text-[0.9rem] hover:text-white transition-colors no-underline">contact@appitep.fr</a></li>
            <li><a href="https://appitep.fr" target="_blank" rel="noopener noreferrer" className="text-slate-400 text-[0.9rem] hover:text-white transition-colors no-underline">appitep.fr</a></li>
          </ul>
        </div>
      </div>
      <div className="max-w-[1200px] mx-auto mt-8 pt-6 border-t border-white/10 text-center text-[0.82rem] text-slate-400">
        <p>&copy; 2026 cantine.appitep.fr — Tous droits réservés.</p>
      </div>
    </footer>
  );
}

/* ───── App Root ───── */

export default function App() {
  const [page, setPage] = useState<Page>("home");
  const [initialScreenshotId, setInitialScreenshotId] = useState<string | undefined>();

  const navigate = (p: Page) => {
    setPage(p);
    setInitialScreenshotId(undefined);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigateToScreenshot = (screenshotId: string) => {
    setInitialScreenshotId(screenshotId);
    setPage("screenshots");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Navbar onNavigate={navigate} />
      {page === "contact" ? (
        <main>
          <ContactForm onNavigate={navigate} />
        </main>
      ) : page === "screenshots" ? (
        <main>
          <ScreenshotGallery onNavigate={navigate} initialScreenshotId={initialScreenshotId} />
        </main>
      ) : (
        <main>
          <Hero onNavigate={navigate} />
          <TargetAudience />
          <BeforeAfter />
          <Benefits />
          <Pillars />
          <Features />
          <ScreenshotPreview onNavigate={navigate} onScreenshotClick={navigateToScreenshot} />
          <Steps />
          <Testimonials />
          <Pricing onNavigate={navigate} />
          <Comparison />
          <FAQ />
          <FinalCTA onNavigate={navigate} />
        </main>
      )}
      <Footer />
    </>
  );
}
